import { ApiError } from "./api-error";

const HOUR_MS = 60 * 60 * 1000;

export interface SourceScheduleOption {
  value: string | null;
  label: string;
}

export const SOURCE_SCHEDULE_OPTIONS: SourceScheduleOption[] = [
  { value: null, label: "Ogni 24h" },
  { value: "@every 72h", label: "Ogni 3 giorni" },
  { value: "@every 168h", label: "Ogni 7 giorni" },
  { value: "@every 12h", label: "Ogni 12h" },
  { value: "@every 6h", label: "Ogni 6h" },
  { value: "@hourly", label: "Ogni 1h" },
  { value: "manual", label: "Solo manuale" },
];

export function normalizeSourceCron(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed || trimmed === "@daily") return null;
  return trimmed;
}

export function validateSourceCron(value: unknown): string | null {
  if (value !== undefined && value !== null && typeof value !== "string") {
    throw new ApiError(400, "Pianificazione non valida: scegli una cadenza supportata o manual");
  }
  const normalized = normalizeSourceCron(value);
  if (normalized !== "manual" && sourceScheduleIntervalMs(normalized) === null) {
    throw new ApiError(400, "Pianificazione non supportata: scegli una cadenza disponibile o manual");
  }
  return normalized;
}

export function sourceScheduleLabel(cron: string | null): string {
  const normalized = normalizeSourceCron(cron);
  const option = SOURCE_SCHEDULE_OPTIONS.find((item) => item.value === normalized);
  return option?.label ?? normalized ?? "Ogni 24h";
}

export function sourceScheduleIntervalMs(cron: string | null): number | null {
  const normalized = normalizeSourceCron(cron);
  if (normalized === null) return 24 * HOUR_MS;
  if (normalized === "manual") return null;
  if (normalized === "@hourly") return HOUR_MS;

  const everyMatch = normalized.match(/^@every\s+(\d+)([hm])$/);
  if (everyMatch) {
    const amount = Number(everyMatch[1]);
    const unit = everyMatch[2];
    const interval = amount * (unit === "h" ? HOUR_MS : 60 * 1000);
    if (!Number.isSafeInteger(amount) || amount <= 0 || !Number.isSafeInteger(interval)) return null;
    return interval;
  }

  switch (normalized) {
    case "0 0 * * *":
      return 24 * HOUR_MS;
    case "0 */12 * * *":
      return 12 * HOUR_MS;
    case "0 */6 * * *":
      return 6 * HOUR_MS;
    case "0 * * * *":
      return HOUR_MS;
    default:
      return null;
  }
}

export function isSourceDue(
  cron: string | null,
  lastRunAt: number | string | null,
  now = Date.now(),
  lastRunStatus?: string | null,
  continuation = false,
): boolean {
  const intervalMs = sourceScheduleIntervalMs(cron);
  if (intervalMs === null) return false;
  const lastRunMs = parseTimestamp(lastRunAt);
  if (lastRunMs === null) return true;
  return lastRunMs + (lastRunStatus === "err" || continuation ? Math.min(intervalMs, HOUR_MS) : intervalMs) <= now;
}

function parseTimestamp(value: number | string | null): number | null {
  if (value === null) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const numeric = Number(value);
  if (Number.isFinite(numeric) && numeric > 0) return numeric;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}
